import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    padding: "12px 16px",
    border: `1px solid ${theme.colors.neutral030}`,
    borderRadius: "4px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    width: "100%",
  },
  status: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  // Flush with the title: no left padding, and none of MUI's negative
  // start-icon margin, so the chevron lines up with the row above.
  toggle: {
    paddingLeft: 0,
    minWidth: 0,
    "& .MuiButton-startIcon": {
      marginLeft: 0,
    },
  },
  list: {
    margin: 0,
    // The toggle's 20px start icon plus its 8px margin, so entries line up
    // with the toggle text.
    padding: "4px 0 0 28px",
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface IpAllowlistSectionProps {
  /** `undefined` until the allowlist has loaded; `[]` means it is off. */
  ipAllowlist: Array<string> | undefined;
  isLoading: boolean;
  isError: boolean;
  onEdit: () => void;
  onRetry: () => void;
}

export const IpAllowlistSection: React.FC<IpAllowlistSectionProps> =
  function IpAllowlistSection({
    ipAllowlist,
    isLoading,
    isError,
    onEdit,
    onRetry,
  }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    // interlude may return the same address twice (`1.2.3.4` and `1.2.3.4/32`).
    const uniqueEntries = useMemo(
      () => (ipAllowlist ? Array.from(new Set(ipAllowlist)) : []),
      [ipAllowlist],
    );

    const handleToggleExpanded = useHandler(() => {
      setExpanded((prev) => !prev);
    });

    return (
      <Box component="section" sx={styles.root}>
        <Box sx={styles.row}>
          <Typography variant="h3" fontWeight={500}>
            {t("apiKey.ipAllowlist.section.title", "IP allowlist")}
          </Typography>
          <Box sx={styles.status}>
            {isLoading ? <CircularProgress size={20} disableShrink /> : null}
            {/*
             * Never fall through to the "off" status on a failed load: reading
             * "off" for an allowlist that is actually on invites the reader to
             * set one up again, or to assume traffic is not being filtered.
             */}
            {ipAllowlist?.length === 0 ? (
              <>
                <Badge variant="plain">
                  {t("apiKey.ipAllowlist.section.status.off", "Off")}
                </Badge>
                <Typography variant="note">
                  {t(
                    "apiKey.ipAllowlist.section.status.offDetail",
                    "Any IP can use these keys",
                  )}
                </Typography>
              </>
            ) : null}
            {uniqueEntries.length > 0 ? (
              <>
                <Badge variant="positive">
                  {t("apiKey.ipAllowlist.section.status.on", "On")}
                </Badge>
                <Typography variant="note">
                  {t("apiKey.ipAllowlist.section.entryCount", "{{count}} IPs", {
                    count: uniqueEntries.length,
                  })}
                </Typography>
              </>
            ) : null}
          </Box>
          <Button
            variant="transparent"
            onClick={onEdit}
            disabled={ipAllowlist === undefined}
            aria-label={t(
              "apiKey.ipAllowlist.section.editButton",
              "Edit allowlist",
            )}
          >
            {t("common.edit")}
          </Button>
        </Box>

        {!isLoading && isError ? (
          <InfoBox
            variant="error"
            title={t(
              "apiKey.ipAllowlist.section.error.title",
              "Could not load the IP allowlist",
            )}
            description={t(
              "apiKey.ipAllowlist.section.error.description",
              "Whether an allowlist is in effect is unknown. Try again before editing it.",
            )}
            action={{
              label: t("apiKey.ipAllowlist.section.error.retry", "Try again"),
              onClick: onRetry,
            }}
          />
        ) : null}

        {uniqueEntries.length > 0 ? (
          <Box>
            <Button
              variant="transparent"
              aria-expanded={expanded}
              onClick={handleToggleExpanded}
              sx={styles.toggle}
              startIcon={
                // Two literal attributes rather than one ternary: UnoCSS only
                // generates an icon's CSS from a literal `un-i-motif="..."` in
                // source, so a computed value renders an empty box.
                expanded ? (
                  <MotifIcon un-i-motif="arrow_up" />
                ) : (
                  <MotifIcon un-i-motif="arrow_down" />
                )
              }
            >
              {expanded
                ? t(
                    "apiKey.ipAllowlist.section.hideEntries",
                    "Hide allowed IPs",
                  )
                : t(
                    "apiKey.ipAllowlist.section.showEntries",
                    "Show allowed IPs",
                  )}
            </Button>
            <Collapse in={expanded} unmountOnExit>
              <Box component="ul" sx={styles.list}>
                {uniqueEntries.map((entry) => (
                  <Box component="li" key={entry}>
                    <Typography
                      variant="body"
                      color={theme.colors.staticFgBody}
                    >
                      {entry}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        ) : null}
      </Box>
    );
  };
